import { Link, graphql, navigate, useStaticQuery } from "gatsby";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUser } from "../../../../store/auth";
import { AppDispatch, RootState } from "../../../../store";
import { salesChannel } from "../../../../config";
import SalesChannelContext from "../../../../context/SalesChannelContext";
import CartDisplay from "../../../ecommerce/CartDisplay";
import OrderInTheNext from "../../../ecommerce/OrderInTheNext";
import Button from "../../../shared/Button";
import { setStep } from "../../../../store/order";
import CheckoutProgression from "./CheckoutProgression";
import { OrderType } from "../../../../types";
import { BsBag } from "react-icons/bs";
import { fetchAllCurrencyCodes } from "../../../../store/currencyCode";
import ecommerceApi from "../../../../services/ecommerceApi";

type Props = {
    seo: {
        title: string;
    };
    cart?: OrderType;
    children?: React.ReactNode;
    hideNav?: boolean;
    showCouponCode?: boolean;
    containerClass?: string;
};

const CheckoutLayout = ({ cart, seo, hideNav = false, showCouponCode = true, containerClass = "max-w-screen-md", children }: Props) => {
    const data = useStaticQuery(graphql`
        query {
            salesChannel {
                id
                name
                email
                logo
                logoFile {
                    publicURL
                }

                phone
                address
                type
                donations {
                    id
                    name
                    desc
                    type
                    value
                    image
                    enabled
                    salesChannel
                }
            }
        }
    `);

    const logoUrl = data?.salesChannel?.logoFile?.publicURL;

    const dispatch = useDispatch<AppDispatch>();

    const order = cart || useSelector((state: RootState) => state.order.order);
    const currencyCode = useSelector((state: RootState) => state.currencyCode?.selectedCurrencyCode?.currencyCode);

    const count = React.useMemo(() => {
        return (order?.orderItems || [])
            .filter(orderItem => !orderItem.parentId)
            ?.reduce((acc, item) => {
                return acc + item.quantity;
            }, 0);
    }, [order?.orderItems]);

    React.useEffect(() => {
        if (!currencyCode) {
            dispatch(fetchAllCurrencyCodes());
        }
    }, []);

    React.useEffect(() => {

        const setCode = async (orderId: string, currencyCode: string) => {
            await ecommerceApi.updateOrder(orderId, { currencyCode });
        }

        if (currencyCode && order?.currencyCode !== currencyCode) {
            if (order?.id) {
                setCode(order.id, currencyCode);
            }
        }
    }, [currencyCode, order?.currencyCode]);

    React.useEffect(() => {
        dispatch(getUser());
    }, []);

    return (
        <SalesChannelContext.Provider value={{ salesChannel: data.salesChannel }}>
            <div className="flex flex-wrap w-full" style={{ minHeight: "100vh" }}>
                <div className="grow px-3 pb-10 md:pb-0">
                    <header className="px-1 md:px-6 pt-5 pb-5 md:pb-10">
                        <div className="flex flex-wrap items-center -mx-3">
                            <div className="md:w-auto px-3">
                                <Link className="inline-block" to="/">
                                    <img
                                        className="h-8 md:h-12 w-auto"
                                        width={"32px"}
                                        height={"32px"}
                                        src={logoUrl}
                                    />
                                </Link>
                            </div>
                            {!hideNav && (
                                <div className="px-3 ml-3 grow hidden xl:block">
                                    <CheckoutProgression />
                                </div>
                            )}
                        </div>
                    </header>

                    <main className={`${containerClass} mx-auto`}>{children}</main>
                </div>

                {!!order && (
                    <div className="w-full xl:w-[500px] px-3 bg-gray-100">
                        <div
                            className={`
                        p-5 }
                        overflow-auto
                        bg-gray-100 flex justify-center
                        xl:static
                    `}
                        >
                            <div className="max-w-[500px]">
                                <div className="mb-6 hidden xl:block">
                                    <OrderInTheNext className="w-full" />
                                </div>

                                <h2 className="text-2xl d mb-3">Shopping Cart</h2>

                                <div>
                                    <CartDisplay
                                        order={order}
                                        textSize="text-base"
                                        options={{
                                            showRemove: false,
                                            allowQuantityChange: false,
                                            showCouponCode
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </SalesChannelContext.Provider>
    );
};

const Badge = ({ children, count }: { children: React.ReactNode; count: number }) => {
    return (
        <div className="relative">
            {children}
            {count > 0 && (
                <span
                    className="
                        absolute top-[-9px] right-[-9px]
                        w-[18px] h-[18px] flex items-center justify-center rounded-full 
                        bg-primary text-white text-xs"
                >
                    {count}
                </span>
            )}
        </div>
    );
};

export default CheckoutLayout;
